import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { instrumentApi } from '../../../../utils/api';

const PERIOD_DAYS = {
    today: 1,
    '7d': 7,
    '30d': 30,
};

function periodToDays(period) {
    if (!period || period === 'all') return undefined;
    return PERIOD_DAYS[period];
}

function _resolveSort(sort, query) {
    if (sort === 'relevance' && !query) return 'newest';
    return sort || 'newest';
}

export default function useInsightFeed({
    query = '',
    symbol,
    canonical_id,
    asset_class,
    category,
    period,
    sort = 'newest',
    pageSize = 20,
} = {}) {
    const trimmedQuery = (query || '').trim();
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const requestId = useRef(0);

    const filterKey = useMemo(() => JSON.stringify({
        q: trimmedQuery, symbol, canonical_id, asset_class, category,
        period_days: periodToDays(period), sort, pageSize,
    }), [trimmedQuery, symbol, canonical_id, asset_class, category, period, sort, pageSize]);

    const reload = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        try {
            const res = await instrumentApi.getFeed({
                q: trimmedQuery || undefined,
                symbol,
                canonical_id,
                asset_class,
                category,
                period_days: periodToDays(period),
                sort: _resolveSort(sort, trimmedQuery),
                limit: pageSize,
            });
            if (id !== requestId.current) return;
            setItems(res.items || []);
            setNextCursor(res.next_cursor || null);
        } catch (err) {
            if (id !== requestId.current) return;
            setError(err?.message || 'Không tải được danh sách báo cáo');
            setItems([]);
            setNextCursor(null);
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [trimmedQuery, symbol, canonical_id, asset_class, category, period, sort, pageSize]);

    const loadMore = useCallback(async () => {
        if (!nextCursor || loadingMore) return;
        const id = ++requestId.current;
        setLoadingMore(true);
        try {
            const res = await instrumentApi.getFeed({
                q: trimmedQuery || undefined,
                symbol,
                canonical_id,
                asset_class,
                category,
                period_days: periodToDays(period),
                sort: _resolveSort(sort, trimmedQuery),
                cursor: nextCursor,
                limit: pageSize,
            });
            if (id !== requestId.current) return;
            setItems((prev) => [...prev, ...(res.items || [])]);
            setNextCursor(res.next_cursor || null);
        } catch (err) {
            if (id !== requestId.current) return;
            setError(err?.message || 'Không tải được trang kế tiếp');
        } finally {
            if (id === requestId.current) setLoadingMore(false);
        }
    }, [nextCursor, loadingMore, trimmedQuery, symbol, canonical_id, asset_class, category, period, sort, pageSize]);

    useEffect(() => {
        reload();
    }, [filterKey]);

    return {
        items,
        loading,
        loadingMore,
        error,
        hasMore: !!nextCursor,
        loadMore,
        reload,
    };
}
